<template lang="pug">
    .media-vault-view(:class="theClass" v-scroll-bottom="scrollBottom")
        template(v-if="modeEdit")
            settings-form-button.media-vault-view__top-button(v-if="selectedAlbumId" title="Back to Albums" @click="backToAlbums")
            settings-form-button.media-vault-view__top-button(v-else title="Create Album" @click="createAlbum")

        .media-vault-view__albums(v-if="!selectedAlbumId && albums.length")
            .media-vault-view__albums-title Albums
            .media-vault-view__albums-grid
                .media-vault-view__album-wrap(v-for="album in albums" :key="album.id")
                    generic-square-card-control.media-vault-view__album(@click="selectAlbum(album)")
                        template
                            svg-icon(iconId="album-icon")
                        template(v-if="modeEdit" v-slot:controls)
                            .media-vault-view__album-edit-actions(@click="openAlbumActions(album, $event)")
                                svg-icon(iconId="menu-dots-2")
                    .media-vault-view__album-title {{ album.name }}

        .media-vault-view__files(v-if="files.length || selectedAlbumId")
            .media-vault-view__files-title {{ mediaListTitle }}
            .media-vault-view__files-grid
                generic-square-card-control.media-vault-view__file(v-for="file in files" :key="file.id" :class="getFileClass(file)" @click="selectFile(file)")
                    template
                        img(v-if="file.type == 'image'" :src="file.url")
                        video-player-control(v-else-if="file.type == 'video'" :asset="file" :fillMode="true" :clickToPlay="false")
                    
                    template(v-slot:controls)
                        .media-vault-view__file-edit-actions(v-if="modeEdit" @click="openFileActions(file, $event)")
                            svg-icon(iconId="menu-dots-2")
                        .media-vault-view__file-select(v-if="modeSelect")
                            svg-icon(iconId="checkbox-check")
        
        template(v-if="!albumsLoading && !filesLoading")
            .media-vault-view__empty-folder(v-if="selectedAlbumId && !files.length")
                span The album is empty.
            .media-vault-view__empty-vault(v-if="!selectedAlbumId && !albums.length && !files.length")
                span Your media vault is empty.
</template>

<script>
import EventBus from '../../lib/event-bus'
import Events from '../../lib/events'
import ModalsList from '../modals/modals-list'
import MediaVaultService from '../../services/media-vault-service'

export default {
    name: 'media-vault-view',

    props: {
        albumId: {
            type: String,
        },
        fileIds: {
            type: Array,
            default() { return [] },
        },
        mode: {
            type: String,
            validator: (v) => !v || ['edit', 'select'].includes(v),
        },
    },

    data() {
        return {
            albums: [],
            files: [],
            filesPagination: null,
            filesLoading: false,
            filesLoadingIdx: 0,
            albumsLoading: false,

            selectedAlbumId: null,
            selectedFileIds: null,
        }
    },

    computed: {
        modeEdit() {
            return this.mode == 'edit'
        },
        modeSelect() {
            return this.mode == 'select'
        },

        mediaListTitle() {
            return this.selectedAlbumId ? this.albums.find(a => a.id == this.selectedAlbumId).name : 'Media'
        },

        filesLastPage() {
            return this.filesPagination && this.filesPagination.current_page == this.filesPagination.total_pages
        },

        theClass() {
            return {
                [`media-vault-view--mode-${this.mode}`]: this.mode,
                'media-vault-view--album-selected': !!this.selectedAlbumId,
            }
        },

        albumActions() {
            return [
                { value: 'rename', name: 'Change name' },
                { value: 'delete', name: 'Delete' },
            ]
        },

        fileActions() {
            return [
                { value: 'view', name: 'View' },
                { value: 'move-to-album', name: 'Move to album' },
                { value: 'delete', name: 'Delete' },
            ]
        },
    },

    watch: {
        albumId: {
            handler(val) {
                this.selectedAlbumId = val
            },
            immediate: true,
        },
        selectedAlbumId(val) {
            this.loadFiles()
            this.$emit(`update:albumId`, val)
        },

        fileIds: {
            handler(val) {
                this.selectedFileIds = val
            },
            immediate: true,
        },
        selectedFileIds(val) {
            this.$emit(`update:fileIds`, val)
        },
    },

    methods: {
        getSelectedAlbumId() {
            return this.selectedAlbumId
        },

        loadFiles(more = false) {
            if (!more) {
                this.filesLoading = false
                this.filesPagination = null
            }

            if (more && (this.filesLoading || this.filesLastPage)) {
                return
            }

            this.filesLoading = true
            const loadingIdx = ++this.filesLoadingIdx

            MediaVaultService.list({
                page: more ? this.filesPagination.current_page + 1 : 1,
                album_id: this.selectedAlbumId,
            }).then(r => {
                if (loadingIdx != this.filesLoadingIdx) {
                    return
                }

                if (more) {
                    this.files.push(...r.data.data)
                }
                else {
                    this.files = r.data.data
                }
                this.filesPagination = r.data.meta.pagination

                this.filesLoading = false
            })
        },

        loadAlbums() {
            this.albumsLoading = true
            MediaVaultService.listAlbums().then(r => {
                this.albums = r.data
                this.albumsLoading = false
            })
        },

        createAlbum() {
            EventBus.$emit(Events.OPEN_MODAL, ModalsList.CREATE_MEDIA_VAULT_ALBUM, {
                created: () => {
                    this.loadAlbums()
                },
            })
        },

        backToAlbums() {
            this.selectedAlbumId = null
            this.files = []
        },

        selectAlbum(album) {
            this.selectedAlbumId = album.id
            this.files = []
        },

        selectFile(file) {
            const idx = this.selectedFileIds.indexOf(file.id)
            if (idx >= 0) {
                this.selectedFileIds.splice(idx, 1)
            }
            else {
                this.selectedFileIds.push(file.id)
            }
        },

        scrollBottom() {
            this.loadFiles(true)
        },

        openAlbumActions(album, ev) {
            EventBus.$emit(Events.OPEN_MODAL, ModalsList.SELECT_OPTION, {
                element: ev.currentTarget,
                options: this.albumActions,
                handler: (action) => this.selectAlbumAction(action, album),
                offsetX: 1,
                placement: 'bottom-end',
            })
        },

        openFileActions(file, ev) {
            EventBus.$emit(Events.OPEN_MODAL, ModalsList.SELECT_OPTION, {
                element: ev.currentTarget,
                options: this.fileActions,
                handler: (action) => this.selectFileAction(action, file),
                offsetX: 1,
                placement: 'bottom-end',
            })
        },

        selectAlbumAction(action, album) {
            switch (action.value) {
                case 'rename':
                    EventBus.$emit(Events.OPEN_MODAL, ModalsList.UPDATE_MEDIA_VAULT_ALBUM, {
                        album,
                        updated: () => {
                            this.loadAlbums()
                        },
                    })
                    break
                case 'delete':
                    EventBus.$emit(Events.OPEN_MODAL, ModalsList.GENERIC_CONFIRMATION, {
                        text: 'Are you sure you want to delete it?',
                        approve: () => {
                            MediaVaultService.deleteAlbum(album.id).then(() => {
                                this.loadAlbums()
                                this.loadFiles()
                            })
                        },
                    })
                    break
            }
        },

        selectFileAction(action, file) {
            switch (action.value) {
                case 'view':
                    break
                case 'move-to-album':
                    EventBus.$emit(Events.OPEN_MODAL, ModalsList.SELECT_MEDIA_VAULT_ALBUM, {
                        selected: (album) => {
                            MediaVaultService.assign(file.id, {
                                albumId: album.id,
                            }).then(() => {
                                this.loadFiles()
                            })
                        },
                        exceptAlbumId: this.selectedAlbumId,
                    })
                    break
                case 'delete':
                    EventBus.$emit(Events.OPEN_MODAL, ModalsList.GENERIC_CONFIRMATION, {
                        text: 'Are you sure you want to delete it?',
                        approve: () => {
                            MediaVaultService.delete(file.id, {
                                albumId: this.selectedAlbumId,
                            }).then(() => {
                                this.loadFiles()
                            })
                        },
                    })
                    break
            }
        },

        getFileClass(file) {
            return {
                'media-vault-view__file--selected': this.selectedFileIds.includes(file.id),
            }
        },
    },

    created() {
        this.loadAlbums()
        this.loadFiles()
    },
}
</script>
